import axios from 'axios';
import { encryptPayload, normalizeEncryptedApiResponse, plainObjectFromFormData } from './customerCrypto';
import { ensureCompanySettings, resolveCustomerApiEncrypt } from './companySettingsCache';

export function joinApiUrl(base, path) {
  return `${String(base || '').replace(/\/+$/, '')}/${String(path || '').replace(/^\/+/, '')}`;
}

export async function postEncryptedForm(url, bearerToken, plainPayload, options = {}) {
  const settings = await ensureCompanySettings();
  const useEncrypt = resolveCustomerApiEncrypt(settings);
  let body;

  if (useEncrypt) {
    const ep = encryptPayload(plainPayload);
    if (!ep) {
      throw new Error('ENCRYPT_KEY_MISSING');
    }
    const fd = new FormData();
    fd.append('encrypted_payload', ep);
    body = fd;
  } else {
    const fd = new FormData();
    Object.entries(plainPayload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => fd.append(`${key}[]`, item));
      } else {
        fd.append(key, value);
      }
    });
    body = fd;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
    },
    body,
    signal: options.signal,
  });

  if (options.parse === 'text') {
    const responseText = await response.text();
    let data = null;
    try {
      const raw = JSON.parse(responseText);
      data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
    } catch {
      data = null;
    }
    return { response, data, responseText, useEncrypt };
  }

  const raw = await response.json().catch(() => null);
  const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
  return { response, data, useEncrypt };
}

export async function postEncryptedJson(url, bearerToken, plainPayload, options = {}) {
  const settings = await ensureCompanySettings();
  const useEncrypt = resolveCustomerApiEncrypt(settings);
  const headers = {
    Authorization: `Bearer ${bearerToken}`,
  };
  let body;

  if (useEncrypt) {
    const ep = encryptPayload(plainPayload);
    if (!ep) {
      throw new Error('ENCRYPT_KEY_MISSING');
    }
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify({ encrypted_payload: ep });
  } else {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(plainPayload);
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body,
    signal: options.signal,
  });

  const raw = await response.json().catch(() => null);
  const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
  return { response, data, useEncrypt };
}

export async function postEncryptedFormFromFormData(url, bearerToken, formData, options = {}) {
  const plain = plainObjectFromFormData(formData);
  return postEncryptedForm(url, bearerToken, plain, options);
}

export async function axiosPostEncryptedForm(url, bearerToken, plainPayload) {
  const settings = await ensureCompanySettings();
  const useEncrypt = resolveCustomerApiEncrypt(settings);
  let body;

  if (useEncrypt) {
    const ep = encryptPayload(plainPayload);
    if (!ep) {
      throw new Error('ENCRYPT_KEY_MISSING');
    }
    body = new FormData();
    body.append('encrypted_payload', ep);
  } else {
    body = new FormData();
    Object.entries(plainPayload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => body.append(`${key}[]`, item));
      } else {
        body.append(key, value);
      }
    });
  }

  const res = await axios.post(url, body, {
    headers: { Authorization: `Bearer ${bearerToken}` },
  });
  return normalizeEncryptedApiResponse(res.data, useEncrypt) ?? res.data;
}
